import React, { useEffect, useState } from "react";
import { api, brl } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function PlansAdminDialog({ open, onClose, onSaved }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/master/plans", { _cache: false });
    setPlans(r.data || []);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const upd = (key, field, value) => {
    setPlans(prev => prev.map(p => p.key === key ? { ...p, [field]: value } : p));
  };

  const save = async (p) => {
    if (!p.name?.trim()) { toast.error("Nome obrigatório"); return; }
    if (p.price == null || isNaN(Number(p.price)) || Number(p.price) < 0) { toast.error("Preço inválido"); return; }
    setBusy(true);
    try {
      await api.put(`/master/plans/${p.key}`, {
        name: p.name, price: Number(p.price), description: p.description || "",
      });
      toast.success(`Plano ${p.name} salvo`);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="plans-admin-dialog">
        <DialogHeader>
          <DialogTitle>Gerenciar planos</DialogTitle>
          <p className="text-xs text-neutral-500 mt-1">As alterações afetam novos cobranças e o faturamento estimado. Histórico de pagamentos não é alterado.</p>
        </DialogHeader>

        <div className="space-y-3">
          {plans.map(p => (
            <div key={p.key} className="p-4 border border-[#E5E5E5] rounded-md" data-testid={`plan-row-${p.key}`}>
              <div className="grid grid-cols-1 sm:grid-cols-[120px,1fr,140px,auto] gap-3 items-end">
                <div>
                  <Label>Chave (fixa)</Label>
                  <Input value={p.key} disabled className="bg-neutral-50 font-mono text-xs"/>
                </div>
                <div>
                  <Label>Nome exibido</Label>
                  <Input value={p.name} onChange={(e)=>upd(p.key, "name", e.target.value)} data-testid={`plan-name-${p.key}`}/>
                </div>
                <div>
                  <Label>Preço mensal</Label>
                  <Input type="number" step="0.01" min="0" value={p.price} onChange={(e)=>upd(p.key, "price", e.target.value)} data-testid={`plan-price-${p.key}`}/>
                  <p className="text-[10px] text-neutral-500 mt-0.5">{brl(p.price)}</p>
                </div>
                <Button onClick={() => save(p)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`plan-save-${p.key}`}>
                  <Save size={14} className="mr-1"/> Salvar
                </Button>
              </div>
              <div className="mt-3">
                <Label>Descrição (opcional)</Label>
                <Input value={p.description || ""} onChange={(e)=>upd(p.key, "description", e.target.value)} placeholder="Ex.: Inclui catálogo, 5 usuários…"/>
              </div>
            </div>
          ))}
          {plans.length === 0 && <div className="text-center text-sm text-neutral-500 py-6">Carregando…</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
